import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  FormHelperText,
  Divider, 
  Grid, 
  Paper, 
  CircularProgress,
  Chip,
  OutlinedInput,
  SelectChangeEvent,
  Tabs,
  Tab,
  Alert
} from '@mui/material';
import { 
  InputMethod, 
  CampaignGoal, 
  AdFormat, 
  TargetAudienceAttributes,
  AdStrategyRequest,
  AdStrategyResponse,
  analyzeUrl,
  createAdStrategy,
  getAdStrategies,
  getAdStrategy
} from '../../services/adStrategistService';
import AuthDebug from './AuthDebug';
import { useNavigate } from 'react-router-dom';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

interface SetupTabProps {
  onStrategyCreated: (strategy: AdStrategyResponse) => void;
  onStrategySelected: (strategy: AdStrategyResponse) => void;
  isCreating: boolean;
}

const SetupTab: React.FC<SetupTabProps> = ({ 
  onStrategyCreated, 
  onStrategySelected,
  isCreating
}) => {
  // Tab state
  const [tabValue, setTabValue] = useState(isCreating ? 0 : 1);
  
  // Form state
  const [inputMethod, setInputMethod] = useState<InputMethod>(InputMethod.URL);
  const [productUrl, setProductUrl] = useState('');
  const [campaignBrief, setCampaignBrief] = useState('');
  const [campaignGoals, setCampaignGoals] = useState<CampaignGoal[]>([]);
  const [adFormats, setAdFormats] = useState<AdFormat[]>([]);
  const [tone, setTone] = useState('');
  const [keywords, setKeywords] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState('');
  
  // Target audience state
  const [targetAudience, setTargetAudience] = useState<TargetAudienceAttributes>({
    age_range: [],
    gender: [],
    interests: [],
    location: [],
    income_level: [],
    education: [],
    occupation: []
  });
  
  // URL analysis state
  const [urlData, setUrlData] = useState<any>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  // Strategy list state
  const [strategies, setStrategies] = useState<any[]>([]);
  const [isLoadingStrategies, setIsLoadingStrategies] = useState(false);
  
  // Loading state
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Validation state
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  
  // Add a new state for polling status
  const [isPolling, setIsPolling] = useState(false);
  
  const navigate = useNavigate();
  
  // Load existing strategies
  useEffect(() => {
    if (tabValue === 1) {
      loadStrategies();
    }
  }, [tabValue]);
  
  const loadStrategies = async () => {
    setIsLoadingStrategies(true);
    try {
      const data = await getAdStrategies();
      setStrategies(data);
    } catch (error) {
      console.error('Error loading strategies:', error);
    } finally {
      setIsLoadingStrategies(false);
    }
  };
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  const handleInputMethodChange = (event: SelectChangeEvent<string>) => {
    setInputMethod(event.target.value as InputMethod);
    // Clear URL-specific data when switching to brief
    if (event.target.value === InputMethod.BRIEF) {
      setUrlData(null);
      setProductUrl('');
    }
  };
  
  const handleCampaignGoalsChange = (event: SelectChangeEvent<CampaignGoal[]>) => {
    const value = event.target.value as unknown as CampaignGoal[];
    setCampaignGoals(value);
  };
  
  const handleAdFormatsChange = (event: SelectChangeEvent<AdFormat[]>) => {
    const value = event.target.value as unknown as AdFormat[];
    setAdFormats(value);
  };
  
  const handleTargetAudienceChange = (
    field: keyof TargetAudienceAttributes,
    event: SelectChangeEvent<string[]>
  ) => {
    const value = event.target.value as unknown as string[];
    setTargetAudience(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const handleAddKeyword = () => {
    if (keywordInput.trim() && !keywords.includes(keywordInput.trim())) {
      setKeywords([...keywords, keywordInput.trim()]);
      setKeywordInput('');
    }
  };
  
  const handleKeywordInputKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddKeyword();
    }
  };
  
  const handleRemoveKeyword = (keywordToRemove: string) => {
    setKeywords(keywords.filter(keyword => keyword !== keywordToRemove));
  };
  
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (inputMethod === InputMethod.URL && !productUrl) {
      newErrors.productUrl = 'Product URL is required';
    }
    
    if (inputMethod === InputMethod.BRIEF && !campaignBrief) {
      newErrors.campaignBrief = 'Campaign brief is required';
    }
    
    if (campaignGoals.length === 0) {
      newErrors.campaignGoals = 'At least one campaign goal is required';
    }
    
    if (adFormats.length === 0) {
      newErrors.adFormats = 'At least one ad format is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleAnalyzeUrl = async () => {
    if (!productUrl) {
      setErrors({ ...errors, productUrl: 'Please enter a valid URL' });
      return;
    }
    
    try {
      setIsAnalyzing(true);
      const data = await analyzeUrl(productUrl);
      setUrlData(data);
      
      // Pre-fill campaign brief with analyzed data
      const briefTemplate = `Product: ${data.title || 'N/A'}\n\n${data.description || ''}\n\nKey Features:\n${(data.main_features || []).map(feature => `- ${feature}`).join('\n')}`;
      setCampaignBrief(briefTemplate);
      
      // Set campaign goals based on the product
      if (data.keywords && data.keywords.length > 0) {
        // Extract potential campaign goals from keywords
        const potentialGoals = [];
        if (data.keywords.some(kw => kw.toLowerCase().includes('new') || kw.toLowerCase().includes('launch'))) {
          potentialGoals.push(CampaignGoal.AWARENESS);
        }
        if (data.keywords.some(kw => kw.toLowerCase().includes('brand') || kw.toLowerCase().includes('awareness'))) {
          potentialGoals.push(CampaignGoal.AWARENESS);
        }
        if (data.keywords.some(kw => kw.toLowerCase().includes('sale') || kw.toLowerCase().includes('promotion'))) {
          potentialGoals.push(CampaignGoal.CONVERSION);
        }
        if (data.keywords.some(kw => kw.toLowerCase().includes('lead') || kw.toLowerCase().includes('conversion'))) {
          potentialGoals.push(CampaignGoal.LEAD_GENERATION);
        }
        if (data.keywords.some(kw => kw.toLowerCase().includes('engage') || kw.toLowerCase().includes('interact'))) {
          potentialGoals.push(CampaignGoal.ENGAGEMENT);
        }
        if (data.keywords.some(kw => kw.toLowerCase().includes('traffic') || kw.toLowerCase().includes('visit'))) {
          potentialGoals.push(CampaignGoal.TRAFFIC);
        }
        if (data.keywords.some(kw => kw.toLowerCase().includes('consider') || kw.toLowerCase().includes('compare'))) {
          potentialGoals.push(CampaignGoal.CONSIDERATION);
        }
        
        // Set campaign goals if any were identified, otherwise set a default
        if (potentialGoals.length > 0) {
          setCampaignGoals(potentialGoals);
        } else {
          setCampaignGoals([CampaignGoal.AWARENESS]);
        }
      }
      
      // Set keywords if available
      if (data.keywords && data.keywords.length > 0) {
        setKeywords(data.keywords);
      }
      
      // Set appropriate tone based on product category
      if (data.metadata && data.metadata.category) {
        const category = data.metadata.category.toLowerCase();
        let suggestedTone = '';
        
        if (category.includes('tech') || category.includes('electronics')) {
          suggestedTone = 'Professional, Innovative, Forward-thinking';
        } else if (category.includes('fashion') || category.includes('clothing')) {
          suggestedTone = 'Trendy, Stylish, Aspirational';
        } else if (category.includes('food') || category.includes('drink')) {
          suggestedTone = 'Warm, Inviting, Appetizing';
        } else if (category.includes('health') || category.includes('fitness')) {
          suggestedTone = 'Motivational, Supportive, Energetic';
        } else if (category.includes('luxury')) {
          suggestedTone = 'Sophisticated, Exclusive, Premium';
        } else {
          suggestedTone = 'Professional, Clear, Engaging';
        }
        
        setTone(suggestedTone);
      }
      
      // Set target audience if available
      if (data.metadata && data.metadata.audience) {
        const audienceStr = data.metadata.audience.toLowerCase();
        const targetAudienceUpdates: Partial<TargetAudienceAttributes> = {};
        
        // Age range
        if (audienceStr.includes('young') || audienceStr.includes('teen')) {
          targetAudienceUpdates.age_range = ['18-24'];
        } else if (audienceStr.includes('professional') || audienceStr.includes('working')) {
          targetAudienceUpdates.age_range = ['25-34', '35-44'];
        } else if (audienceStr.includes('senior') || audienceStr.includes('elder')) {
          targetAudienceUpdates.age_range = ['55-64', '65+'];
        }
        
        // Interests based on product category
        if (data.metadata.category) {
          const category = data.metadata.category.toLowerCase();
          if (category.includes('tech')) {
            targetAudienceUpdates.interests = ['Technology'];
          } else if (category.includes('fashion')) {
            targetAudienceUpdates.interests = ['Fashion'];
          } else if (category.includes('health')) {
            targetAudienceUpdates.interests = ['Health'];
          } else if (category.includes('sport')) {
            targetAudienceUpdates.interests = ['Sports'];
          } else if (category.includes('education')) {
            targetAudienceUpdates.interests = ['Education'];
          } else if (category.includes('finance')) {
            targetAudienceUpdates.interests = ['Finance'];
          }
        }
        
        setTargetAudience({ ...targetAudience, ...targetAudienceUpdates });
      }
      
      // Set default ad formats based on analyzed data
      setAdFormats([AdFormat.SOCIAL_MEDIA_POST, AdFormat.SEARCH_AD]);
      
    } catch (error) {
      console.error('Error analyzing URL:', error);
      setErrors({ ...errors, productUrl: 'Failed to analyze URL. Please try again.' });
    } finally {
      setIsAnalyzing(false);
    }
  };
  
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const request: AdStrategyRequest = {
        input_method: inputMethod,
        product_url: inputMethod === InputMethod.URL ? productUrl : undefined,
        campaign_brief: inputMethod === InputMethod.BRIEF ? campaignBrief : undefined,
        campaign_goals: campaignGoals,
        target_audience: targetAudience,
        ad_formats: adFormats,
        tone: tone || undefined,
        keywords: keywords.length > 0 ? keywords : undefined
      };
      
      const response = await createAdStrategy(request);
      onStrategyCreated(response);
      
    } catch (error) {
      console.error('Error creating strategy:', error);
      setErrors({ submit: 'Failed to create strategy. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleStrategySelect = async (strategyId: number) => {
    try {
      // Fetch the complete strategy with all details
      const fullStrategy = await getAdStrategy(strategyId);
      console.log('Selected strategy:', fullStrategy);
      
      // If the strategy is still pending or processing, set up polling and show loading state
      if (fullStrategy.status === 'pending' || fullStrategy.status === 'processing') {
        // Start polling for updates
        setIsPolling(true);
        setGenerationError("Strategy generation in progress. Please wait...");
        
        // Set up polling interval
        const pollingInterval = setInterval(async () => {
          try {
            const updatedStrategy = await getAdStrategy(strategyId);
            console.log('Polling selected strategy status:', updatedStrategy.status);
            
            // If strategy is complete or has error, stop polling
            if (updatedStrategy.status === 'completed') {
              clearInterval(pollingInterval);
              setIsPolling(false);
              setGenerationError(null);
              console.log('Selected strategy completed, navigating to creation tab:', updatedStrategy);
              onStrategySelected(updatedStrategy);
            } else if (updatedStrategy.status === 'error' || updatedStrategy.status === 'failed') {
              clearInterval(pollingInterval);
              setIsPolling(false);
              setGenerationError(`Error generating strategy: ${updatedStrategy.error_message || 'Unknown error'}`);
            }
          } catch (error) {
            console.error('Error polling strategy status:', error);
            clearInterval(pollingInterval);
            setIsPolling(false);
            setGenerationError("Failed to check strategy status. Please try again.");
          }
        }, 5000); // Poll every 5 seconds
      } else if (fullStrategy.status === 'error' || fullStrategy.status === 'failed') {
        // Show error message if strategy generation failed
        setGenerationError(`Strategy generation failed: ${fullStrategy.error_message || 'Unknown error'}`);
      } else if (fullStrategy.status === 'completed') {
        // Pass the complete strategy to the parent component
        console.log('Selected completed strategy, navigating to creation tab:', fullStrategy);
        onStrategySelected(fullStrategy);
      } else {
        // Unknown status
        console.error('Unknown strategy status:', fullStrategy.status);
        setGenerationError(`Unknown strategy status: ${fullStrategy.status}`);
      }
    } catch (error) {
      console.error('Error selecting strategy:', error);
      setGenerationError("Failed to load strategy. Please try again.");
    }
  };
  
  const handleGenerateStrategy = async () => {
    setIsGenerating(true);
    setGenerationError(null);
    
    try {
        // Validate form before submission
        if (!validateForm()) {
            setIsGenerating(false);
            setGenerationError("Please fill in all required fields");
            return;
        }
      
        // Debug: log the request payload before sending
        const requestPayload = {
            input_method: inputMethod,
            product_url: productUrl,
            campaign_brief: campaignBrief,
            campaign_goals: campaignGoals,
            target_audience: targetAudience,
            ad_formats: adFormats,
            tone: tone,
            keywords: keywords
        };
        
        console.log('Request payload:', JSON.stringify(requestPayload, null, 2));
        
        // Check authentication before making the request
        const token = localStorage.getItem('token');
        if (!token) {
            setGenerationError("Authentication required. Please log in again.");
            setIsGenerating(false);
            return;
        }
        
        const response = await createAdStrategy(requestPayload);
        
        console.log('Strategy creation response:', response);
        
        // Check if the response has the expected format
        if (response && response.id && response.status === 'pending') {
          // Show pending message
          setGenerationError("Strategy generation in progress. This may take a few minutes...");
          
          // Set up polling for status updates
          const pollingInterval = setInterval(async () => {
            try {
              const updatedStrategy = await getAdStrategy(response.id);
              console.log('Polling strategy status:', updatedStrategy.status);
              
              // If strategy is complete or has error, stop polling
              if (updatedStrategy.status === 'completed') {
                clearInterval(pollingInterval);
                console.log('Strategy completed, navigating to creation tab:', updatedStrategy);
                onStrategyCreated(updatedStrategy);
                setIsGenerating(false);
              } else if (updatedStrategy.status === 'error' || updatedStrategy.status === 'failed') {
                clearInterval(pollingInterval);
                setIsGenerating(false);
                setGenerationError(`Error generating strategy: ${updatedStrategy.error_message || 'Unknown error'}`);
              } else if (updatedStrategy.status === 'processing') {
                // Update the message to show progress
                setGenerationError("Strategy generation in progress... This may take a few minutes. Please wait.");
              }
            } catch (error) {
              console.error('Error polling strategy status:', error);
              clearInterval(pollingInterval);
              setIsGenerating(false);
              setGenerationError("Failed to check strategy status. Please try again.");
            }
          }, 5000); // Poll every 5 seconds
        } else if (response && response.id) {
          // Strategy may already be completed
          try {
            console.log('Strategy may be completed already, fetching details');
            const fullStrategy = await getAdStrategy(response.id);
            console.log('Fetched full strategy:', fullStrategy);
            onStrategyCreated(fullStrategy);
          } catch (fetchError) {
            console.error('Error fetching complete strategy:', fetchError);
            setGenerationError("Failed to load the complete strategy. Please check the Existing Strategies tab.");
          }
        } else {
          // Unexpected response format
          console.error('Unexpected response format:', response);
          setGenerationError("Received an unexpected response from the server. Please try again.");
        }
    } catch (error: any) {
        console.error('Error creating strategy:', error);
        
        // Enhanced error reporting
        const errorData = error.response?.data;
        const errorDetail = errorData?.detail || 'Unknown error';
        const errorStatus = error.response?.status;
        
        // Log detailed error information
        console.error('Error details:', {
            status: errorStatus,
            message: error.message,
            detail: errorDetail,
            data: errorData
        });
        
        // Provide user-friendly error messages based on status codes
        if (errorStatus === 401) {
            setGenerationError("Authentication error: Your session may have expired. Please log in again.");
        } else if (errorStatus === 400) {
            setGenerationError(`Invalid request: ${errorDetail}`);
        } else if (errorStatus === 500) {
            setGenerationError("Server error: The server encountered an issue. Please try again later.");
        } else if (error.message && error.message.includes("Network Error")) {
            setGenerationError("Network error: Unable to connect to the server. Please check your internet connection.");
        } else {
            setGenerationError(`Error: ${errorDetail} (${errorStatus || 'Unknown'})`);
        }
    } finally {
        setIsGenerating(false);
    }
  };
  
  // Add a function to map campaign goal values to user-friendly labels
  const getCampaignGoalLabel = (goal: CampaignGoal): string => {
    const labels: Record<CampaignGoal, string> = {
      [CampaignGoal.AWARENESS]: 'Brand Awareness',
      [CampaignGoal.CONSIDERATION]: 'Product Consideration',
      [CampaignGoal.CONVERSION]: 'Sales Conversion',
      [CampaignGoal.ENGAGEMENT]: 'User Engagement',
      [CampaignGoal.TRAFFIC]: 'Website Traffic',
      [CampaignGoal.LEAD_GENERATION]: 'Lead Generation',
      [CampaignGoal.PRODUCT_LAUNCH]: 'Product Launch',
      [CampaignGoal.BRAND_AWARENESS]: 'Brand Awareness',
      [CampaignGoal.CUSTOMER_RETENTION]: 'Customer Retention',
      [CampaignGoal.MARKET_EXPANSION]: 'Market Expansion'
    };
    return labels[goal] || goal;
  };
  
  return (
    <Box>
      <AuthDebug />
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Create New Strategy" />
          <Tab label="Existing Strategies" />
        </Tabs>
      </Box>
      
      {tabValue === 0 && (
        <Box>
          <Typography variant="h5" gutterBottom>Create Ad Strategy</Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom>Input Method</Typography>
                
                <FormControl fullWidth margin="normal" error={!!errors.inputMethod}>
                  <InputLabel>Input Method</InputLabel>
                  <Select
                    value={inputMethod}
                    onChange={handleInputMethodChange}
                    label="Input Method"
                  >
                    <MenuItem value={InputMethod.URL}>Product URL</MenuItem>
                    <MenuItem value={InputMethod.BRIEF}>Campaign Brief</MenuItem>
                  </Select>
                  {errors.inputMethod && <FormHelperText>{errors.inputMethod}</FormHelperText>}
                </FormControl>
                
                {inputMethod === InputMethod.URL ? (
                  <>
                    <FormControl fullWidth margin="normal" error={!!errors.productUrl}>
                      <TextField
                        label="Product URL"
                        value={productUrl}
                        onChange={(e) => setProductUrl(e.target.value)}
                        error={!!errors.productUrl}
                        helperText={errors.productUrl}
                        disabled={isAnalyzing}
                      />
                    </FormControl>
                    
                    <Button
                      variant="outlined"
                      onClick={handleAnalyzeUrl}
                      disabled={isAnalyzing || !productUrl}
                      sx={{ mt: 1 }}
                    >
                      {isAnalyzing ? <CircularProgress size={24} /> : 'Analyze URL'}
                    </Button>
                    
                    {urlData && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                        <Typography variant="subtitle1" gutterBottom color="primary">URL Analysis Results:</Typography>
                        <Typography variant="body2"><strong>Title:</strong> {urlData.title || 'Not available'}</Typography>
                        <Typography variant="body2"><strong>Description:</strong> {urlData.description || 'Not available'}</Typography>
                        
                        {urlData.keywords && urlData.keywords.length > 0 && (
                          <>
                            <Typography variant="body2" mt={1}><strong>Keywords:</strong></Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                              {urlData.keywords.map((keyword: string, index: number) => (
                                <Chip key={index} label={keyword} size="small" />
                              ))}
                            </Box>
                          </>
                        )}
                        
                        {urlData.main_features && urlData.main_features.length > 0 && (
                          <>
                            <Typography variant="body2" mt={1}><strong>Main Features:</strong></Typography>
                            <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                              {urlData.main_features.map((feature: string, index: number) => (
                                <li key={index}><Typography variant="body2">{feature}</Typography></li>
                              ))}
                            </ul>
                          </>
                        )}
                        
                        {urlData.pricing_info && (
                          <Typography variant="body2" mt={1}><strong>Price:</strong> {urlData.pricing_info}</Typography>
                        )}
                      </Box>
                    )}
                  </>
                ) : (
                  <FormControl fullWidth margin="normal" error={!!errors.campaignBrief}>
                    <TextField
                      label="Campaign Brief"
                      multiline
                      rows={4}
                      value={campaignBrief}
                      onChange={(e) => setCampaignBrief(e.target.value)}
                      error={!!errors.campaignBrief}
                      helperText={errors.campaignBrief || "Describe your product, audience, and goals"}
                    />
                  </FormControl>
                )}
                
                <Typography variant="h6" sx={{ mt: 4 }}>Campaign Goals</Typography>
                
                <FormControl fullWidth margin="normal" error={!!errors.campaignGoals}>
                  <InputLabel>Campaign Goals</InputLabel>
                  <Select
                    multiple
                    value={campaignGoals}
                    onChange={handleCampaignGoalsChange}
                    input={<OutlinedInput label="Campaign Goals" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={getCampaignGoalLabel(value)} />
                        ))}
                      </Box>
                    )}
                    MenuProps={MenuProps}
                  >
                    {Object.values(CampaignGoal).map((goal) => (
                      <MenuItem key={goal} value={goal}>
                        {getCampaignGoalLabel(goal)}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.campaignGoals && <FormHelperText>{errors.campaignGoals}</FormHelperText>}
                </FormControl>
                
                <Typography variant="h6" sx={{ mt: 4 }}>Ad Formats</Typography>
                
                <FormControl fullWidth margin="normal" error={!!errors.adFormats}>
                  <InputLabel>Ad Formats</InputLabel>
                  <Select
                    multiple
                    value={adFormats}
                    onChange={handleAdFormatsChange}
                    input={<OutlinedInput label="Ad Formats" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value.replace('_', ' ')} />
                        ))}
                      </Box>
                    )}
                    MenuProps={MenuProps}
                  >
                    {Object.values(AdFormat).map((format) => (
                      <MenuItem key={format} value={format}>
                        {format.replace('_', ' ')}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.adFormats && <FormHelperText>{errors.adFormats}</FormHelperText>}
                </FormControl>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom>Target Audience</Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Age Range</InputLabel>
                      <Select
                        multiple
                        value={targetAudience.age_range || []}
                        onChange={(e) => handleTargetAudienceChange('age_range', e)}
                        input={<OutlinedInput label="Age Range" />}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                              <Chip key={value} label={value} />
                            ))}
                          </Box>
                        )}
                        MenuProps={MenuProps}
                      >
                        {['18-24', '25-34', '35-44', '45-54', '55-64', '65+'].map((age) => (
                          <MenuItem key={age} value={age}>{age}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Gender</InputLabel>
                      <Select
                        multiple
                        value={targetAudience.gender || []}
                        onChange={(e) => handleTargetAudienceChange('gender', e)}
                        input={<OutlinedInput label="Gender" />}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                              <Chip key={value} label={value} />
                            ))}
                          </Box>
                        )}
                        MenuProps={MenuProps}
                      >
                        {['Male', 'Female', 'Non-binary', 'All'].map((gender) => (
                          <MenuItem key={gender} value={gender}>{gender}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Interests</InputLabel>
                      <Select
                        multiple
                        value={targetAudience.interests || []}
                        onChange={(e) => handleTargetAudienceChange('interests', e)}
                        input={<OutlinedInput label="Interests" />}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                              <Chip key={value} label={value} />
                            ))}
                          </Box>
                        )}
                        MenuProps={MenuProps}
                      >
                        {['Technology', 'Fashion', 'Sports', 'Health', 'Education', 'Finance', 'Travel', 'Food', 'Entertainment'].map((interest) => (
                          <MenuItem key={interest} value={interest}>{interest}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                
                <Typography variant="h6" sx={{ mt: 4 }}>Additional Settings</Typography>
                
                <FormControl fullWidth margin="normal">
                  <TextField
                    label="Tone"
                    value={tone}
                    onChange={(e) => setTone(e.target.value)}
                    placeholder="e.g., Professional, Friendly, Humorous"
                  />
                </FormControl>
                
                <Typography variant="subtitle1" sx={{ mt: 2 }}>Keywords</Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TextField
                    label="Add Keyword"
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyDown={handleKeywordInputKeyDown}
                    sx={{ flex: 1 }}
                  />
                  <Button 
                    variant="contained" 
                    onClick={handleAddKeyword} 
                    disabled={!keywordInput.trim()}
                    sx={{ ml: 1 }}
                  >
                    Add
                  </Button>
                </Box>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {keywords.map((keyword) => (
                    <Chip
                      key={keyword}
                      label={keyword}
                      onDelete={() => handleRemoveKeyword(keyword)}
                    />
                  ))}
                </Box>
              </Paper>
            </Grid>
          </Grid>
          
          {errors.submit && (
            <Typography color="error" sx={{ mt: 2 }}>
              {errors.submit}
            </Typography>
          )}
          
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleGenerateStrategy}
              disabled={isGenerating}
              sx={{ minWidth: 200 }}
            >
              {isGenerating ? <CircularProgress size={24} /> : 'Generate Ad Strategy'}
            </Button>
          </Box>
        </Box>
      )}
      
      {tabValue === 1 && (
        <Box>
          <Typography variant="h5" gutterBottom>Load Existing Strategy</Typography>
          
          {isLoadingStrategies ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : strategies.length === 0 ? (
            <Alert severity="info">
              No existing strategies found. Create your first ad strategy!
            </Alert>
          ) : (
            <Grid container spacing={2}>
              {strategies.map((strategy) => (
                <Grid item xs={12} sm={6} md={4} key={strategy.id}>
                  <Paper 
                    elevation={2} 
                    sx={{ 
                      p: 2, 
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'background.paper', boxShadow: 3 },
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%'
                    }}
                    onClick={() => handleStrategySelect(strategy.id)}
                  >
                    <Typography variant="h6" noWrap component="div">
                      {strategy.input_method === InputMethod.URL ? 'URL: ' : 'Brief: '}
                      {strategy.input_method === InputMethod.URL 
                        ? (strategy.product_url?.length > 30 
                            ? strategy.product_url.substring(0, 30) + '...' 
                            : strategy.product_url)
                        : (strategy.campaign_brief?.length > 30 
                            ? strategy.campaign_brief.substring(0, 30) + '...' 
                            : strategy.campaign_brief)}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Created: {new Date(strategy.created_at).toLocaleDateString()}
                    </Typography>
                    
                    {strategy.status && (
                      <Box sx={{ mt: 1 }}>
                        {strategy.status === 'pending' ? (
                          <Chip 
                            label="Generating..." 
                            color="warning" 
                            size="small"
                            icon={<CircularProgress size={16} color="inherit" />} 
                          />
                        ) : strategy.status === 'completed' ? (
                          <Chip label="Complete" color="success" size="small" />
                        ) : strategy.status === 'error' ? (
                          <Chip label="Error" color="error" size="small" />
                        ) : (
                          <Chip label={strategy.status} size="small" />
                        )}
                      </Box>
                    )}
                    
                    {strategy.status === 'error' && strategy.error_message && (
                      <Typography 
                        variant="caption" 
                        color="error" 
                        sx={{ mt: 1, display: 'block' }}
                      >
                        {strategy.error_message.length > 50 
                          ? strategy.error_message.substring(0, 50) + '...' 
                          : strategy.error_message}
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}
    </Box>
  );
};

export default SetupTab; 